import { useState, useEffect } from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { getOrderById, getCMSContent } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { Search, Package, CheckCircle, Truck, Clock, XCircle, ShoppingBag, PartyPopper, ExternalLink } from 'lucide-react';

const CARRIER_LABELS = {
  colissimo:     'Colissimo (La Poste)',
  chronopost:    'Chronopost',
  ups:           'UPS',
  mondial_relay: 'Mondial Relay',
  dpd:           'DPD',
  fedex:         'FedEx',
};
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const STATUS_STEPS = [
  { key: 'pending', label: 'Reçue', sublabel: 'Commande reçue', icon: Clock },
  { key: 'confirmed', label: 'Acceptée', sublabel: 'Acceptée par la boutique', icon: CheckCircle },
  { key: 'preparing', label: 'En préparation', sublabel: 'En cours de préparation', icon: Package },
  { key: 'shipped', label: 'Envoyée', sublabel: 'En cours de livraison', icon: Truck },
  { key: 'delivered', label: 'Livrée', sublabel: 'Commande livrée', icon: PartyPopper },
];

const STATUS_KEY_MAP = {
  pending: 0,
  confirmed: 1,
  preparing: 2,
  shipped: 3,
  delivered: 4,
};

const STATUS_LABELS = {
  pending: 'En attente de confirmation',
  confirmed: 'Acceptée — En préparation',
  preparing: 'En préparation',
  shipped: 'Envoyée',
  delivered: 'Livrée',
  cancelled: 'Annulée',
};

const PAYMENT_LABELS = {
  pending: 'En attente de paiement',
  paid: 'Payée',
  failed: 'Échec du paiement',
};

const StatusTimeline = ({ status }) => {
  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl border border-red-100">
        <XCircle className="w-6 h-6 text-red-500 shrink-0" />
        <p className="text-red-700 font-medium">Cette commande a été annulée.</p>
      </div>
    );
  }

  const currentStep = STATUS_KEY_MAP[status] ?? 0;

  return (
    <div className="relative">
      <div className="flex items-start justify-between relative">
        {/* Progress line background */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-stone-200 z-0" />
        {/* Progress line filled */}
        <div
          className="absolute top-5 left-0 h-0.5 bg-primary z-0 transition-all duration-700"
          style={{ width: `${(currentStep / (STATUS_STEPS.length - 1)) * 100}%` }}
        />

        {STATUS_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isDone = idx <= currentStep;
          const isActive = idx === currentStep;
          return (
            <div key={step.key} className="flex flex-col items-center z-10 flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                isDone ? 'bg-primary border-primary' : 'bg-white border-stone-200'
              } ${isActive ? 'shadow-lg shadow-primary/25 scale-110' : ''}`}>
                <Icon className={`w-5 h-5 ${isDone ? 'text-white' : 'text-stone-400'}`} />
              </div>
              <p className={`text-xs mt-2 text-center font-medium leading-tight max-w-[70px] ${
                isActive ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {step.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function SuiviCommandePage() {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [phone, setPhone] = useState('');

  useEffect(() => {
    getCMSContent().then(r => {
      if (r.data?.phone_number) setPhone(r.data.phone_number);
    }).catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmed = orderId.trim();
    if (!trimmed) return;
    setLoading(true);
    setOrder(null);
    setSearched(false);
    try {
      const response = await getOrderById(trimmed);
      setOrder(response.data);
    } catch {
      toast.error('Commande introuvable. Vérifiez votre identifiant.');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const STATUS_LABELS = {
    pending: 'En attente de confirmation',
    confirmed: 'Confirmée — en préparation',
    shipped: 'Expédiée',
    delivered: 'Livrée',
    cancelled: 'Annulée',
  };

  const PAYMENT_LABELS = {
    pending: 'En attente de paiement',
    paid: 'Payée',
    failed: 'Échec du paiement',
  };

  return (
    <PublicLayout>
      <SEO
        title="Suivi de Commande"
        description="Suivez l'avancement de votre commande Villa Marceau en temps réel grâce à votre numéro de commande au format VM-000001."
        canonical="/suivi-commande"
      />
      <section className="pt-12 pb-8 px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs text-primary tracking-[0.3em] uppercase mb-3">Villa Marceau</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-2">
              Suivi de commande
            </h1>
            <p className="text-muted-foreground mb-8">
              Entrez votre numéro de commande pour suivre son avancement (format : VM-000001).
            </p>

            <form onSubmit={handleSearch} className="flex gap-3">
              <Input
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Numéro de commande (ex : VM-000001)"
                className="flex-1"
                data-testid="order-id-input"
              />
              <Button type="submit" disabled={loading || !orderId.trim()} data-testid="track-order-btn">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Order details */}
      {order && (
        <section className="py-6 px-6 lg:px-12">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Status card */}
              <Card className="border-border/60">
                <CardContent className="p-6 space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Commande</p>
                      <p className="font-mono text-sm text-foreground break-all">{order.id}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-muted-foreground mb-1">{formatDate(order.created_at)}</p>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {PAYMENT_LABELS[order.payment_status] || order.payment_status}
                      </span>
                    </div>
                  </div>

                  <StatusTimeline status={order.status} />

                  <div className="pt-2">
                    <p className="text-sm font-medium text-foreground">
                      Statut : <span className="text-primary">{STATUS_LABELS[order.status] || order.status}</span>
                    </p>
                  </div>

                  {/* Tracking link when shipped */}
                  {order.status === 'shipped' && order.tracking_number && (
                    <div className="mt-4 bg-purple-50 border border-purple-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-5 h-5 text-purple-600" />
                        <p className="font-semibold text-purple-800 text-sm">Votre colis est en cours de livraison</p>
                      </div>
                      <div className="space-y-1 text-sm mb-3">
                        <p className="text-purple-700">
                          <span className="font-medium">Transporteur :</span> {CARRIER_LABELS[order.carrier] || order.carrier}
                        </p>
                        <p className="text-purple-700">
                          <span className="font-medium">N° de suivi :</span>{' '}
                          <span className="font-mono font-bold tracking-wider">{order.tracking_number}</span>
                        </p>
                      </div>
                      {order.tracking_url && (
                        <a
                          href={order.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                          data-testid="tracking-external-link"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Suivre mon colis
                        </a>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Customer info */}
              <Card className="border-border/60">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold mb-4">Informations de livraison</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p><span className="text-foreground font-medium">Nom :</span> {order.customer_name}</p>
                    <p><span className="text-foreground font-medium">Email :</span> {order.customer_email}</p>
                    {order.customer_phone && (
                      <p><span className="text-foreground font-medium">Téléphone :</span> {order.customer_phone}</p>
                    )}
                    <p><span className="text-foreground font-medium">Adresse :</span> {order.shipping_address}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Items */}
              <Card className="border-border/60">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold mb-4">Articles commandés</h3>
                  <div className="space-y-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <div>
                          <p className="font-medium text-foreground">{item.product_name}</p>
                          <p className="text-muted-foreground">Qté : {item.quantity}</p>
                        </div>
                        <p className="font-semibold text-primary">{(item.price * item.quantity).toFixed(2)} €</p>
                      </div>
                    ))}
                    <div className="pt-3 border-t border-border flex justify-between font-semibold">
                      <span>Total</span>
                      <span className="text-primary text-lg">{order.total.toFixed(2)} €</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <p className="text-sm text-muted-foreground text-center">
                Une question ?{' '}
                {phone && <span>Appelez-nous : <a href={`tel:${phone}`} className="text-primary font-medium">{phone}</a> — ou </span>}
                Rendez-vous à{' '}
                <Link to="/ou-nous-trouver" className="text-primary hover:underline">
                  notre boutique
                </Link>
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {searched && !order && (
        <section className="py-6 px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
            <p className="text-muted-foreground">Aucune commande trouvée avec cet identifiant.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Vérifiez l'email de confirmation que vous avez reçu pour retrouver votre identifiant.
            </p>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
